// "View on Map" (T17) — resolves the table's spatial column at CLICK TIME (menu build can't know
// column types) and opens a Spatial Explorer tab preloaded with the table as layer 1. Native-spatial
// (and, once T18 lands, declared) columns are found via the existing table-metadata IPC — cheap, no
// row fetch. SQLite additionally needs ONE bounded LIMIT 50 sample to sniff BLOB-affinity geometry
// columns (A23/NG20): a BLOB column is indistinguishable from any other BLOB by metadata alone.
// `detectFirstSpatialColumn` THROWS when a metadata/sample fetch fails (rejects or resolves
// `success: false`) — that is an infra failure, distinct from a table genuinely having zero spatial
// columns, so `handleViewOnMap` shows a DIFFERENT toast for it and skips the `outcome: 'no-spatial'`
// analytics event (Checkpoint-4 LOW-a).

import { toast } from '../../../components/ui/toaster';
import { getConnectionById } from '../../connections/utils/connectionLookup';
import type { ObjectActionContext } from '../../shared/object-actions';
import type { DatabaseEngineId } from '@/core/engines';
import { fetchMetaInfo } from '../../workspace/common/services/metaInfoApi';
import {
  categorizeSpatialColumn,
  sniffSpatialBlobColumns,
  type SpatialColumnInput,
} from '../../workspace/common/utils/spatialColumnCategorization';
import type { SpatialLayerGeometryRef } from '../../workspace/spatial/types/spatialTypes';
import { fetchTableExplorerRows } from '../../workspace/table-explorer/services/tableExplorerApi';
import { safeConnectionAlias, safeConnectionServer } from '@/types/safeConnectionDisplay';

/** A23/NG20 — the ONE bounded sample query sqlite runs when metadata alone finds no spatial column. */
const SQLITE_SNIFF_SAMPLE_LIMIT = 50;

function tableLabel(schema: string, table: string): string {
  return schema ? `${schema}.${table}` : table;
}

/**
 * Resolve the first spatial column to preload as layer 1, or `null` when the table has none.
 * **Native is preferred over a declaration** (iterate native first): a real geometry column must
 * win over a declared lat-only column that happens to sort earlier. A declared match emits a
 * `declaration` ref (NOT a column ref) — a declared pair is not decodable as a single column, so a
 * column ref would open a dead layer (fetch → decode-fail). The `declaration` ref is what
 * `useSpatialLayerData`'s declared arm consumes.
 */
export async function detectFirstSpatialColumn(params: {
  connectionId: string;
  database: string;
  schema: string;
  table: string;
  engine: DatabaseEngineId;
}): Promise<SpatialLayerGeometryRef | null> {
  const { connectionId, database, schema, table, engine } = params;
  const getTableMetadata = window.electronAPI?.getTableMetadata;
  if (!getTableMetadata) return null;

  // getTableMetadata is awaited WITHOUT a `.catch(() => null)` here (unlike `fetchMetaInfo`, whose
  // declarations are an optional enhancement) — a rejection OR a resolved `success: false` both
  // mean "we couldn't check this table", which must propagate as a thrown error, never collapse
  // into the same `columns = []` path a genuinely spatial-free table takes (Checkpoint-4 LOW-a: a
  // transient connection drop mid-check was wrongly showing the informational "no geometry
  // columns" toast instead of an error).
  const [metaInfo, metaResult] = await Promise.all([
    fetchMetaInfo(connectionId, database).catch(() => null),
    getTableMetadata({ connectionId, database, schema, tableName: table }),
  ]);
  if (!metaResult.success) {
    throw new Error(metaResult.error ?? `Failed to read metadata for ${tableLabel(schema, table)}`);
  }
  const columns = metaResult.metadata?.columns ?? [];
  if (columns.length === 0) return null;

  const inputs: SpatialColumnInput[] = columns.map((c) => ({
    engine,
    schema,
    table,
    name: c.name,
    dataType: c.dataType,
  }));

  // 1) Native wins — scan for a real spatial column FIRST.
  const native = inputs.find((i) => categorizeSpatialColumn(i, metaInfo) === 'native-spatial');
  if (native) return { kind: 'column', column: native.name };

  // 2) A declared coordinate column → a declaration ref (resolve the declaration id).
  const declared = inputs.find((i) => categorizeSpatialColumn(i, metaInfo) === 'declared-spatial');
  if (declared) {
    const decl = metaInfo?.spatialColumns.find(
      (d) => d.schema === schema && d.table === table && d.column === declared.name,
    );
    if (decl) return { kind: 'declaration', declarationId: decl.id };
  }

  if (engine !== 'sqlite') return null;

  // 3) SQLite BLOB-affinity columns need actual values to sniff — one bounded sample query. Same
  // rule as the metadata fetch above: a rejection or `success: false` is a FETCH FAILURE (thrown),
  // never silently treated as "sampled fine, found nothing" (Checkpoint-4 LOW-a).
  const executionId = `spatial-view-on-map-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const sample = await fetchTableExplorerRows({
    connectionId,
    executionId,
    table: { database, schema, tableName: table },
    columns: '*',
    limit: SQLITE_SNIFF_SAMPLE_LIMIT,
    offset: 0,
    filters: [],
    sorting: [],
  });
  if (!sample.success) {
    throw new Error(sample.error ?? `Failed to sample ${tableLabel(schema, table)}`);
  }
  const promoted = sniffSpatialBlobColumns(inputs, sample.rows ?? []);
  const [first] = promoted;
  return first ? { kind: 'column', column: first } : null;
}

/**
 * "View on Map" — validates the table has a spatial column, then opens a Spatial Explorer tab
 * preloaded with it as layer 1. No spatial column ⇒ an informational toast, no tab (never a dead
 * workspace). Analytics fires AFTER validation resolves the outcome.
 */
export async function handleViewOnMap(context: ObjectActionContext): Promise<void> {
  const { connectionId, databaseName, schemaName, objectName } = context;
  const { activeConnection, connection, engine } = getConnectionById(connectionId);
  if (!activeConnection || !connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }
  const engineId = engine as DatabaseEngineId;
  const label = tableLabel(schemaName, objectName);

  let geometryRef: SpatialLayerGeometryRef | null;
  try {
    geometryRef = await detectFirstSpatialColumn({
      connectionId,
      database: databaseName,
      schema: schemaName,
      table: objectName,
      engine: engineId,
    });
  } catch {
    // Infra failure (metadata fetch rejected / errored, or the sqlite sniff sample failed) — this
    // is NOT "checked and found nothing", so it gets a DISTINCT error toast and must NOT fire the
    // `outcome: 'no-spatial'` analytics event (that outcome means "validated, none found") —
    // Checkpoint-4 LOW-a.
    toast.error({ title: 'Error', description: `Couldn't read ${label} — check the connection` });
    return;
  }

  if (!geometryRef) {
    toast.default({ title: `No geometry columns found in ${label}` });
    window.electronAPI?.analytics
      ?.track('feature:spatial_view_on_map', { engine: engineId, outcome: 'no-spatial' })
      .catch(() => { /* fire-and-forget */ });
    return;
  }

  const [{ useWorkspaceStore }, { makeLayer }] = await Promise.all([
    import('../../workspace/core/store/workspaceStore'),
    import('../../workspace/spatial-explorer/store/spatialExplorerActions'),
  ]);

  const layer = makeLayer({
    source: {
      kind: 'table',
      schema: schemaName,
      table: objectName,
      geometryRef,
    },
    label,
    existing: [],
  });

  useWorkspaceStore.getState().addTab({
    title: `Map: ${objectName}`,
    type: 'spatial-explorer',
    connectionInfo: {
      connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection.connection),
      database: databaseName,
    },
    content: {
      connectionId,
      databaseName,
      view: { layers: [layer], basemap: 'default' },
    },
  });

  window.electronAPI?.analytics
    ?.track('feature:spatial_view_on_map', { engine: engineId, outcome: 'opened' })
    .catch(() => { /* fire-and-forget */ });
}
